<template>
  <div
    :class="additionalCampaignsClasses"
    data-ui="additional-campaigns"
  >
    <CdrText
      class="additional__campaign-heading"
      tag="h2"
    >
      {{ heading }}
    </CdrText>
    <CdrText
      class="additional__campaign-subheading"
      tag="h3"
    >
      {{ subheading }}
    </CdrText>
    <div class="cards__container">
      <div
        v-for="({
          cardTitle,
          pillarTitle,
          cardSummary,
          cardImage,
          cardImageAlt,
          buttonCta,
        }, index) in campaigns"
        :key="index"
        class="card"
      >
        <CdrImg
          class="card__image"
          :src="cardImage"
          :alt="cardImageAlt"
        />
        <div class="card__content">
          <CdrText tag="h3">
            <CdrText
              class="card__sub-title"
              tag="small"
            >
              {{ pillarTitle }}
            </CdrText>
            <CdrText
              class="card__title"
              tag="a"
              :href="buttonCta.target"
            >
              {{ cardTitle }}
            </CdrText>
          </CdrText>
          <CdrText
            class="card__text"
            tag="p"
          >
            {{ cardSummary }}
          </CdrText>
          <CdrButton
            modifier="secondary"
            class="card__cta"
            tag="a"
            :href="buttonCta.target"
          >
            {{ buttonCta.text }}
          </CdrButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CdrText, CdrImg, CdrButton } from '@rei/cedar';

export default {
  name: 'AdditionalCampaignsComponent',
  components: {
    CdrText,
    CdrImg,
    CdrButton,
  },
  props: {
    campaigns: {
      type: Array,
      required: true,
    },
    showMembershipPromoTest: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    additionalCampaignsClasses() {
      return {
        additional__campaigns: true,
        'additional__campaigns--no-border': this.showMembershipPromoTest,
      };
    },
    heading() {
      return this.showMembershipPromoTest
        ? 'Your voice makes a difference'
        : 'Don’t stop there!';
    },
    subheading() {
      return this.showMembershipPromoTest
        ? 'Keep it up! Help us support these campaigns.'
        : 'Add your voice to these other campaigns that need your support.';
    },
  },
};
</script>

<style lang="scss">

.additional__campaigns {
  grid-column: 1/3;
  grid-row: 4;
  border-top: 2px solid $cdr-color-border-primary;
  margin-top: $cdr-space-three-x;
}

.additional__campaigns--no-border {
  border: none;
}

.additional__campaigns .additional__campaign-heading {
  @include cdr-text-heading-sans-500;
  text-align: center;
  color: $cdr-color-text-primary;
  margin-bottom: $cdr-space-half-x;
  margin-top: $cdr-space-one-and-a-half-x;
}

.additional__campaigns .additional__campaign-subheading {
  @include cdr-text-body-300;
  text-align: center;
  color: $cdr-color-text-primary;
}

.additional__campaigns .cards__container {
  display: flex;
  padding: $cdr-space-one-x $cdr-space-quarter-x;
  overflow-x: auto;
}

.additional__campaigns .card {
  box-shadow: 0px 0.4rem 0.4rem rgba(12, 11, 8, 0.2);
  border-radius: $cdr-radius-softer;
  transition: all .25s ease;
  flex-grow: 1;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  width: 83%;
  max-width: 284px;

  @include cdr-sm-mq-up {
    flex-basis: 0;
    flex-shrink: auto;
    width: auto;
    max-width: unset;
  }
}

.additional__campaigns .card:not(:last-child) {
  margin-right: $cdr-space-one-x;

  @include cdr-md-mq-up {
    margin-right: $cdr-space-two-x;
  }

}

.additional__campaigns .card:hover {
  box-shadow: 0 0.8rem 0.8rem 0 rgb(12 11 8 / 20%);
  transform: translateY(-0.2rem);
  cursor: pointer;
}

.additional__campaigns .card__content {
  padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-two-x;
  @include cdr-text-heading-serif-900;
  display: flex;
  flex-direction: column;
  height: calc(100% - 124px);

  @include cdr-sm-mq-up {
    height: calc(100% - 118px);
  }

  @include cdr-md-mq-up {
    height: calc(100% - 184px);
  }
}

.additional__campaigns .card__image {
  height: 142px;
  width: 100%;
  -o-object-fit: cover;
  object-fit: cover;

  @include cdr-sm-mq-up {
    height: 118px;
  }

  @include cdr-md-mq-up {
    height: 184px;
  }
}

.additional__campaigns .card__title {
  @include cdr-text-heading-serif-500;
  margin-bottom: $cdr-space-half-x;
  color: $cdr-color-text-primary;
  text-decoration: none;
  display: block;
}

.additional__campaigns .card:hover .card__title {
  text-decoration: underline;
}

.additional__campaigns .card__sub-title {
  @include cdr-text-eyebrow-100;
  margin-bottom: $cdr-space-half-x;
  color: $cdr-color-text-secondary;
  display: block;
}

.additional__campaigns .card__text {
  @include cdr-text-body-300;
  flex: 1;
  color: $cdr-color-text-primary;
  margin-bottom: $cdr-space-one-x;
}

.additional__campaigns .card__cta {
  @include cdr-text-heading-sans-300;
  background: #F9F8F6;
  border: 1px solid #928B80;
  border-radius: 4px;
  width: fit-content;
  max-width: 100%;

  @include cdr-sm-mq-up {
    padding-left: $cdr-space-quarter-x;
    padding-right: $cdr-space-quarter-x;
  }

   @include cdr-md-mq-up {
    padding-left: $cdr-space-half-x;
    padding-right: $cdr-space-half-x;
  }
}

</style>
